import React, { useState, useEffect, useRef } from 'react';
import { useUsuario } from '../../context/usuarioContext';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toolbar } from 'primereact/toolbar';
import { exportarExcel } from '../../utils/exportarExcel'
import { exportarPDF } from '../../utils/pdf/pdfDataTable';
import AgregarProducto from '../agregar-producto/agregar-producto';
import AgregarNotaCredito from '../agregar-nota-credito/agregar-nota-credito';
import RestarDialog from '../restar-producto/restar-producto';
import Permiso from '../../components/wrappers/permiso';
import { agregarProductos, restarExistencias, agregarNotaCredito } from "../../services/inventarioService";
import "./inventory-table.css"
import useMedia from '../../hooks/media-querys'

export default function InventoryTable({ 
  data = [], 
  title = "Productos", 
  onRowSelect, 
  onActualizar 
}) {
  const isPhone  = useMedia('(max-width: 640px)');
  const labelPdf   = isPhone ? 'PDF'   : 'Exportar PDF';
  const labelExcel = isPhone ? 'Excel' : 'Exportar Excel';
  const labelNC = isPhone ? 'Nota Crédito' : 'Nota de Crédito'
  const fixWidth = isPhone ? "140px" : "auto";
  const fixSize = isPhone ? "0.75rem" : "1rem";

  const { tienePermiso } = useUsuario();
  const puedeRestar = tienePermiso('Inventario', 'INV_DESCONTAR_PRODUCTO');

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [filters, setFilters] = useState({
    global: { 
      value: null, 
      matchMode: FilterMatchMode.CONTAINS 
    }
  });

  const [filteredvalue, setFilteredvalue] = useState([]);
  const [globalFilterValue, setGlobalFilterValue] = useState('');

  // Estado para ventana modal agregar producto y nota de credito
  const [addProductVisible, setAddProductVisible] = useState(false);
  const [notaCreditoVisible, setNotaCreditoVisible] = useState(false);

  // Estado para ventana de restar productos
  const [showRestarDialog, setShowRestarDialog] = useState(false);
  const [productoRestar, setProductoRestar] = useState(null);
  const [cantidadRestar, setCantidadRestar] = useState(null); 

  const toast = useRef(null);
  const dt = useRef(null);

  useEffect(() => {
    const transformed = data.map((item) => {
      const stockNum = Number(item.stock ?? 0);
      // Si el stock es 0 => cantidad debe mostrarse 0 (producto “agotado” en unidades)
      const cantidadAjustada = stockNum <= 0 ? 0 : item.cantidad;

      return {
        ...item,
        cantidad: cantidadAjustada,
        estado: calcularEstado(cantidadAjustada),
      };
    });

    setProducts(transformed);
  }, [data]);

  const calcularEstado = (cantidad) => {
    if (cantidad === 0) return 'AGOTADO';
    if (cantidad < 3) return 'BAJO';
    return 'DISPONIBLE';
  };

  const getSeverity = (estado) => {
    switch (estado) {
      case 'AGOTADO': return 'danger';
      case 'BAJO': return 'warning';
      case 'DISPONIBLE': return 'success';
      default: return null;
    }
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;

    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);

    // Registros filtrados
    const filtrado = products.filter((item) => {
      return Object.values(item).some((val) =>
        String(val).toLowerCase().includes(value.toLowerCase())
      );
    });
    setFilteredvalue(filtrado);
  };

  // Helpers
  const asText = (v) => (v == null ? '' : String(v));

  // === Columnas para .xlsx ===
  const columnasExcel = [
    { header: 'Código Barra',   key: 'codigoBarra',    width: 16, get: r => asText(r.codigoBarra) },
    { header: 'Code',           key: 'codigoCode',     width: 12, get: r => asText(r.codigoCode) },
    { header: 'Nombre',         key: 'nombre',         width: 22, get: r => asText(r.nombre) },
    { header: 'Marca',          key: 'marca',          width: 18, get: r => asText(r.marca) },
    { header: 'Descripción',    key: 'descripcion',    width: 30, get: r => asText(r.descripcion) },
    { header: 'Proveedor',      key: 'nombreProveedor',width: 24, get: r => asText(r.nombreProveedor) },
    { header: 'Cantidad',       key: 'cantidad',       width: 10, get: r => asText(r.cantidad) },
    { header: 'Unidad',         key: 'unidad',         width: 10, get: r => asText(r.unidad) },
    { header: 'Stock Actual',   key: 'stock',          width: 14, get: r => asText(r.stock) },
    { header: 'Precio Unitario',key: 'precioUnitario', width: 14, get: r => asText(r.precioUnitario) },
    { header: 'Bodega',         key: 'bodega',         width: 16, get: r => asText(r.bodega) },
    { header: 'Estado',         key: 'estado',         width: 12, get: r => asText(r.estado) },
  ];

  const columnasPDF = [
    { header: 'Código Barra',   get: r => asText(r.codigoBarra), width: 40 }, 
    { header: 'Code',           get: r => asText(r.codigoCode), width: 40 },
    { header: 'Nombre',         get: r => asText(r.nombre)},
    { header: 'Marca',          get: r => asText(r.marca), width: 55 },
    { header: 'Descripción',    get: r => asText(r.descripcion)},
    { header: 'Proveedor',      get: r => asText(r.nombreProveedor)},
    { header: 'Cant',           get: r => asText(r.cantidad), width: 22 },
    { header: 'Stock Actual',   get: r => asText(r.stock + ' ' + r.unidad), width: 45 },
    { header: 'Precio Unitario',get: r => asText(r.precioUnitario), width: 40 },
    { header: 'Bodega',         get: r => asText(r.bodega), width: 43 },
    { header: 'Estado',         get: r => asText(r.estado), width: 58 },
  ];

  const startToolbar = () => {
    const nombreArchivo = 'resumen-inventario';
    const tituloArchivo = 'RESUMEN: INVENTARIO';
    const subTituloArchivo = 'INVENTARIO';
    
    const rows = (filteredvalue.length > 0 ? filteredvalue : products)
      .map(({ productoId, ...otros }) => ({
        ...otros,
        codigoBarra: `${otros.codigoBarra}` || '', // Forzar a texto
        codigoCode: `'${otros.codigoCode}'`|| '',
        codigoOem: `'${otros.codigoOem}'`|| '',
      }));
    
    return (
      <div className='toolbar-group'>
        <Permiso modulo="Inventario" permiso="INV_EXPORTAR_PDF">
          <Button 
            className='btn-pdf'
            label={labelPdf}
            style={{ fontSize: fixSize, width: fixWidth }}
            icon="pi pi-file-pdf"
            disabled={rows.length === 0}
            onClick={() => exportarPDF(
              {rows, columns: columnasPDF, nombreArchivo, tituloArchivo, subTituloArchivo }
            )}
          />
        </Permiso>
        <Permiso modulo="Inventario" permiso="INV_EXPORTAR_EXCEL">
          <Button 
            className='btn-excel'
            label={labelExcel}
            style={{ fontSize: fixSize, width: fixWidth}}
            icon="pi pi-file-excel"
            disabled={rows.length === 0}
            onClick={() => 
              exportarExcel({ rows, columns: columnasExcel, filename: `${nombreArchivo}.xlsx`})
            }
          />
        </Permiso>
      </div>
    )
  };

  const endToolbar = () => {
    return (
      <>
        <div className='toolbar-group'>
          <Permiso modulo="Inventario" permiso="INV_AGREGAR_NOTACREDITO">
            <Button 
              className="inv-btn-outline" 
              label={labelNC}
              style={{ fontSize: fixSize, width: fixWidth}}
              icon="pi pi-plus"
              onClick={() => setNotaCreditoVisible(true)}
            />
          </Permiso>
          <Permiso modulo="Inventario" permiso="INV_AGREGAR_PRODUCTO">        
            <Button 
              className="inv-btn-add"
              label="Productos"
              style={{ fontSize: fixSize, width: fixWidth}}
              icon="pi pi-plus"
              onClick={() => setAddProductVisible(true)}
            />
          </Permiso>
        </div>  

        <AgregarNotaCredito
          visible={notaCreditoVisible}
          onHide={() => setNotaCreditoVisible(false)}
          onSubmit={ async (datos) => {

            try {
              await agregarNotaCredito(datos);
              toast.current.show({ 
                severity: 'success', summary: 'Éxito',
                detail: 'Comprobante y productos enviados correctamente.',
                life: 3000
              })

              // Refrescar listado de productos
              if (onActualizar) { await onActualizar(); }
              setNotaCreditoVisible(false);
            } catch (error) {
              toast.current.show({ severity: 'error', summary: 'Error',
                detail: 'Ocurrio un error al intentar agregar la nota de credito.',
                life: 4000
              });
            }
          }}
        />

        <AgregarProducto
          visible={addProductVisible}
          onHide={() => setAddProductVisible(false)}
          onSubmit={ async (datos) => {
            try {
              await agregarProductos(datos);
              toast.current.show({ severity: 'success', summary: 'Éxito',
              detail: 'Comprobante y productos agregados correctamente.',
              life: 3000
              })

              // Refrescar listado de productos
              if (onActualizar) { await onActualizar(); }
              setAddProductVisible(false);

            } catch (error) {
              toast.current.show({ severity: 'error', summary: 'Error',
                detail: 'Ocurrio un error al intentar agregar los productos.',
                life: 4000
              });
            }
          }}
        />
      </>
    );
  };

  const renderHeader = () => {
    const HeadingTag = isPhone ? "h5" : "h4";
    return (
      <div className="flex flex-wrap gap-2 justify-content-between align-items-center">
        <HeadingTag className="m-0">{title}</HeadingTag>
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search pr-2" />
          <InputText 
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            className={`pl-5 ${isPhone ? "input-text-s" : ""}`}
            placeholder="Buscar..."
          />
        </IconField>
      </div>
    )
  };

  const estadoBodyTemplate = (rowData) => (
    <Tag
      value={rowData.estado} 
      className="inv-tag-status"
      severity={getSeverity(rowData.estado)} />
  );

  const actionBodyTemplate = (rowData) => (
    <div className="flex flex-wrap justify-content-center gap-0">
      <Button 
        className="inv-btn-restar" 
        icon="pi pi-minus"
        tooltip="Restar unidades"
        tooltipOptions={{ position: 'top' }}
        onClick={() => abrirDialogoRestar(rowData)}
        disabled={!puedeRestar || rowData.cantidad <= 0}
      />
    </div>
  );

  const abrirDialogoRestar = (producto) => {
    setProductoRestar(producto);
    setCantidadRestar('');
    setShowRestarDialog(true);
  };

  const confirmarRestar = async () => {
    const cantidadRestarNum = Number(cantidadRestar);
    
    if (!cantidadRestarNum || isNaN(cantidadRestarNum) || cantidadRestarNum <= 0) {
      toast.current?.show({ severity: 'error', summary: 'Cantidad inválida',
        detail: 'Cantidad inválida.',
      });
      return;
    }

    if (cantidadRestarNum > productoRestar.stock) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Cantidad excedida',
        detail: 'No puede restar más de lo que hay en stock.',
      });
      return;
    }
    
    const payload = {
      productoId: productoRestar.productoId,
      medida: productoRestar.medida,
      disminuir: cantidadRestar
    }

    try {
      await restarExistencias(payload);
      toast.current.show({ severity: 'success', summary: 'Éxito', 
        detail: 'Stock actualizado' 
      });
      
      // Refrescar listado de productos
      if (onActualizar) await onActualizar();

      setShowRestarDialog(false);
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Error', 
        detail: 'Ocurrio un error al intentar restar el stock',
        life: 4000
      });
    }
  };

  const unidadDeMedida = (rowData) => {
    return `${rowData.medida} ${rowData.unidad}`;
  };

  const stockActual = (rowData) => {
    return `${rowData.stock} ${rowData.unidad}`;
  };

  const precioUnitario = (rowData) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(rowData.precioUnitario);
  }

  return (
    <div className="table-container card">
      <Toast ref={toast} position="center"/>
      <Toolbar className='inv-toolbar' start={startToolbar} end={endToolbar}></Toolbar>
      <DataTable
        className="inv-data-table"
        ref={dt}
        value={products}
        size='small'
        stripedRows
        header={renderHeader()}
        paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        dataKey="productoId"
        selectionMode="single"
        selection={selectedProduct}
        onSelectionChange={(e) => {
          setSelectedProduct(e.value);
          if (onRowSelect) onRowSelect(e.value);
        }}
        filters={filters}
        filteredvalue={filteredvalue.length > 0 ? filteredvalue : null}
        filterDisplay="menu"
        globalFilterFields={['codigoBarra', 'codigoCode', 'codigoOem', 'nombre', 'marca', 'nombreProveedor', 'unidad', 'medida', 'precioUnitario', 'stock', 'estado', 'bodega']}
        emptyMessage="No hay productos disponibles."
        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} productos"
      >
        <Column field="codigoBarra" header="Código Barra" sortable/>
        <Column field="codigoCode" header="Code" sortable/>
        <Column field="nombre" header="Nombre" sortable/>
        <Column field="marca" header="Marca" sortable/>
        <Column field="descripcion" header="Descripción"/>
        <Column field="nombreProveedor" header="Proveedor" sortable />
        <Column field="unidadDeMedida"  header="Unidad" body={unidadDeMedida} exportBody={unidadDeMedida}/>
        <Column field="cantidad" header="Cantidad"  align={'center'} style={{ textAlign: 'center', justifyContent: 'center'}} sortable />
        <Column field="stockActual" header="Stock Actual" body={stockActual} exportBody={unidadDeMedida}/>
        <Column field="precioUnitario" header="Precio Unitario" sortable body={precioUnitario} exportBody={precioUnitario}/>
        <Column field="bodega" header="Bodega" sortable />
        <Column field="estado" header="Estado" align={'center'} style={{textAlign: 'center'}} body={estadoBodyTemplate} exportBody={(row) => row.estado}/>
        <Column header="Acción" align={'center'} style={{ textAlign: 'center', justifyContent: 'center'}} body={actionBodyTemplate}/>
      </DataTable>

      {/* Dialogo para restar existencias a un producto */}
      <RestarDialog
        visible={showRestarDialog}
        onHide={() => setShowRestarDialog(false)}
        producto={productoRestar}
        disminuir={cantidadRestar}
        setDisminuir={setCantidadRestar}
        onConfirmar={confirmarRestar}
      />
    </div>
  );
}

